import { useState, useEffect, useRef, type ChangeEvent } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TextStyle, FontFamily, Color, FontSize } from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import { authFetch } from "@/lib/authFetch";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import {
  Mail, ChevronRight, Loader2, Send, Eye, CheckCircle2, XCircle, Code2,
  Bold, Italic, Underline as UnderlineIcon, AlignLeft, AlignCenter, AlignRight,
  List, ListOrdered, Link2, ChevronDown, Settings2, Braces, Upload, ImageIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";

// ── Types ─────────────────────────────────────────────────────────────────────

type EmailTemplate = {
  id: string;
  slug: string;
  categorie: string;
  nom: string;
  description: string | null;
  objet: string;
  contenuHtml: string;
  contenuTexte: string | null;
  variablesDisponibles: string[] | null;
  declencheur: string;
  actif: boolean;
  updatedAt: string;
};

type EmailSettings = {
  id: string;
  headerMode: string;
  headerLogoUrl: string | null;
  headerImageUrl: string | null;
  headerGradientStart: string;
  headerGradientEnd: string;
  headerTexte: string;
  footerAdresse: string;
  footerSiret: string;
  footerEmail: string;
  footerSite: string;
};

const DEFAULT_SETTINGS: EmailSettings = {
  id: "",
  headerMode: "couleurs",
  headerLogoUrl: null,
  headerImageUrl: null,
  headerGradientStart: "#4e63f0",
  headerGradientEnd: "#2facf3",
  headerTexte: "Jobotto",
  footerAdresse: "10 Rue de la Vieille Vigne, 91300 Massy",
  footerSiret: "977 944 487 00023",
  footerEmail: "contact@jobotto.fr",
  footerSite: "www.jobotto.fr",
};

const CATEGORIE_LABELS: Record<string, string> = {
  clients: "Clients",
  partenaires: "Partenaires",
  portail: "Portail",
  sav: "SAV",
  commercial: "Commercial",
  equipe: "Équipe",
};

const DECLENCHEUR_LABELS: Record<string, string> = {
  manuel: "Manuel",
  automatique: "Automatique",
};

const FONT_FAMILIES = ["Arial", "Georgia", "Helvetica", "Times New Roman"];
const FONT_SIZES = ["12", "14", "16", "18", "20", "24"];

const FAKE_VARS: Record<string, string> = {
  prenom: "Marie",
  nom: "Dupont",
  lien_connexion: "https://app.jobotto.fr",
  prenom_contact: "Marie",
  nom_societe: "Société Demo",
  email_support: "support@jobotto.fr",
  email_portail: "demo@portail.jobotto.fr",
  lien_portail: "https://portail.jobotto.fr",
  lien_definition_mdp: "https://portail.jobotto.fr/set-password?token=test",
  email_contact: "marie@demo.fr",
  nom_document: "Guide de démarrage rapide",
  categorie_document: "Installation",
  titre_promotion: "Offre Printemps 2026",
  accroche_promotion: "Profitez de 10% de remise sur les pièces détachées.",
  titre_actualite: "Nouveau modèle CC1 Pro disponible",
  accroche_actualite: "Découvrez les améliorations du nouveau CC1 Pro.",
  numero_devis: "DEV-001",
  raison_refus: "Le délai de livraison demandé ne peut pas être respecté.",
  numero_ticket: "TKT-001",
  titre_ticket: "Robot CC1 — Problème de navigation",
};

function interpolate(text: string): string {
  let out = text;
  for (const [k, v] of Object.entries(FAKE_VARS)) {
    out = out.replace(new RegExp(`{{${k}}}`, "g"), v);
  }
  return out;
}

// ── Header & Footer Config Modal ───────────────────────────────────────────────

function HeaderFooterModal({
  open,
  initial,
  onClose,
  onSaved,
}: {
  open: boolean;
  initial: EmailSettings;
  onClose: () => void;
  onSaved: (s: EmailSettings) => void;
}) {
  const [form, setForm] = useState<EmailSettings>(initial);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const logoRef = useRef<HTMLInputElement>(null);
  const imageRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  useEffect(() => { if (open) setForm(initial); }, [open, initial]);

  const handleLogoUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await authFetch("/api/email-settings/logo", { method: "POST", body: fd });
      if (!res.ok) throw new Error((await res.json()).message);
      const updated: EmailSettings = await res.json();
      setForm(f => ({ ...f, headerLogoUrl: updated.headerLogoUrl }));
      toast({ title: "Sigle mis à jour" });
    } catch (e: any) {
      toast({ title: "Erreur upload sigle", description: e.message, variant: "destructive" });
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleImageUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await authFetch("/api/email-settings/header-image", { method: "POST", body: fd });
      if (!res.ok) throw new Error((await res.json()).message);
      const updated: EmailSettings = await res.json();
      setForm(f => ({ ...f, headerImageUrl: updated.headerImageUrl }));
      toast({ title: "Bandeau mis à jour" });
    } catch (e: any) {
      toast({ title: "Erreur upload bandeau", description: e.message, variant: "destructive" });
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await authFetch("/api/email-settings", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          headerMode: form.headerMode,
          headerGradientStart: form.headerGradientStart,
          headerGradientEnd: form.headerGradientEnd,
          headerTexte: form.headerTexte,
          footerAdresse: form.footerAdresse,
          footerSiret: form.footerSiret,
          footerEmail: form.footerEmail,
          footerSite: form.footerSite,
        }),
      });
      if (!res.ok) throw new Error((await res.json()).message);
      const updated: EmailSettings = await res.json();
      // Conserver les URLs uploadées (non retournées par PUT)
      const merged: EmailSettings = {
        ...updated,
        headerLogoUrl: form.headerLogoUrl,
        headerImageUrl: form.headerImageUrl,
      };
      onSaved(merged);
      toast({ title: "Configuration sauvegardée" });
      onClose();
    } catch (e: any) {
      toast({ title: "Erreur", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const isModeImage = form.headerMode === "image";

  // Preview live du bandeau
  const renderPreview = () => {
    if (isModeImage && form.headerImageUrl) {
      return (
        <div className="rounded-lg overflow-hidden border border-border h-[120px]">
          <img src={form.headerImageUrl} alt="bandeau" className="w-full h-full object-cover" />
        </div>
      );
    }
    const gradStyle = { background: `linear-gradient(to right, ${form.headerGradientStart}, ${form.headerGradientEnd})` };
    return (
      <div
        className="rounded-lg overflow-hidden border border-border h-[120px] flex flex-col items-center justify-center gap-1.5"
        style={gradStyle}
      >
        {form.headerLogoUrl
          ? <img src={form.headerLogoUrl} alt="sigle" style={{ height: 40, width: "auto" }} />
          : <ImageIcon className="h-8 w-8 text-white/50" />}
        <span className="text-white text-sm font-bold" style={{ fontSize: 16 }}>
          {form.headerTexte || "Jobotto"}
        </span>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Configuration de l'en-tête & du pied de page</DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-1">

          {/* Prévisualisation live */}
          {renderPreview()}

          {/* Sélection du mode */}
          <div className="space-y-2">
            <Label>Mode d'en-tête</Label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setForm(f => ({ ...f, headerMode: "couleurs" }))}
                data-testid="button-mode-couleurs"
                className={cn(
                  "flex-1 py-2 rounded-lg border text-sm font-medium transition-colors",
                  !isModeImage
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border hover:bg-secondary"
                )}
              >
                Couleurs
              </button>
              <button
                type="button"
                onClick={() => setForm(f => ({ ...f, headerMode: "image" }))}
                data-testid="button-mode-image"
                className={cn(
                  "flex-1 py-2 rounded-lg border text-sm font-medium transition-colors",
                  isModeImage
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border hover:bg-secondary"
                )}
              >
                Image
              </button>
            </div>
          </div>

          {/* Mode Couleurs */}
          {!isModeImage && (
            <>
              {/* Sigle */}
              <div className="space-y-2">
                <Label>Sigle / Logo (40 px de hauteur)</Label>
                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => logoRef.current?.click()}
                    disabled={uploadingLogo}
                    data-testid="button-upload-logo"
                  >
                    {uploadingLogo
                      ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                      : <Upload className="w-4 h-4 mr-1.5" />}
                    {form.headerLogoUrl ? "Changer le sigle" : "Uploader un sigle"}
                  </Button>
                  {form.headerLogoUrl && (
                    <span className="text-xs text-green-600 dark:text-green-400">Sigle enregistré</span>
                  )}
                </div>
                <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
              </div>

              {/* Texte */}
              <div className="space-y-1.5">
                <Label>Texte de l'en-tête</Label>
                <Input
                  value={form.headerTexte}
                  onChange={e => setForm(f => ({ ...f, headerTexte: e.target.value }))}
                  data-testid="input-header-texte"
                />
              </div>

              {/* Couleurs dégradé */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Couleur gauche</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={form.headerGradientStart}
                      onChange={e => setForm(f => ({ ...f, headerGradientStart: e.target.value }))}
                      className="w-10 h-8 rounded cursor-pointer border border-border"
                      data-testid="input-gradient-start"
                    />
                    <Input
                      value={form.headerGradientStart}
                      onChange={e => setForm(f => ({ ...f, headerGradientStart: e.target.value }))}
                      className="h-8 text-xs font-mono"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Couleur droite</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={form.headerGradientEnd}
                      onChange={e => setForm(f => ({ ...f, headerGradientEnd: e.target.value }))}
                      className="w-10 h-8 rounded cursor-pointer border border-border"
                      data-testid="input-gradient-end"
                    />
                    <Input
                      value={form.headerGradientEnd}
                      onChange={e => setForm(f => ({ ...f, headerGradientEnd: e.target.value }))}
                      className="h-8 text-xs font-mono"
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Mode Image */}
          {isModeImage && (
            <div className="space-y-3">
              <div className="rounded-lg bg-secondary/50 border border-border px-4 py-3 text-sm text-muted-foreground flex items-start gap-2">
                <span className="text-base leading-none mt-0.5">📐</span>
                <span>Dimensions recommandées : <strong>600 × 120 px</strong> — Format PNG ou JPG</span>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => imageRef.current?.click()}
                  disabled={uploadingImage}
                  data-testid="button-upload-header-image"
                >
                  {uploadingImage
                    ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    : <Upload className="w-4 h-4 mr-1.5" />}
                  {form.headerImageUrl ? "Changer le bandeau" : "Uploader un bandeau"}
                </Button>
                {form.headerImageUrl && (
                  <span className="text-xs text-green-600 dark:text-green-400">Bandeau enregistré</span>
                )}
              </div>
              <input ref={imageRef} type="file" accept="image/png,image/jpeg,image/jpg" className="hidden" onChange={handleImageUpload} />
            </div>
          )}

          <Separator />

          {/* Footer fields */}
          <div className="space-y-3">
            <p className="text-sm font-medium">Pied de page</p>
            <div className="space-y-1.5">
              <Label className="text-xs">Adresse</Label>
              <Input
                value={form.footerAdresse}
                onChange={e => setForm(f => ({ ...f, footerAdresse: e.target.value }))}
                data-testid="input-footer-adresse"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">SIRET</Label>
              <Input
                value={form.footerSiret}
                onChange={e => setForm(f => ({ ...f, footerSiret: e.target.value }))}
                data-testid="input-footer-siret"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Email</Label>
                <Input
                  value={form.footerEmail}
                  onChange={e => setForm(f => ({ ...f, footerEmail: e.target.value }))}
                  data-testid="input-footer-email"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Site web</Label>
                <Input
                  value={form.footerSite}
                  onChange={e => setForm(f => ({ ...f, footerSite: e.target.value }))}
                  data-testid="input-footer-site"
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="button-save-email-settings">
            {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── HTML View Modal ────────────────────────────────────────────────────────────

function HtmlViewModal({ open, html, onClose }: { open: boolean; html: string; onClose: () => void }) {
  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Code2 className="w-4 h-4" />
            Code HTML généré
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground">Lecture seule — ce code est généré automatiquement par l'éditeur.</p>
        <div className="flex-1 overflow-auto rounded-lg bg-zinc-950 dark:bg-zinc-900 border border-border">
          <pre className="p-4 text-xs text-green-400 whitespace-pre-wrap break-all font-mono">{html}</pre>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { navigator.clipboard.writeText(html); }}>Copier</Button>
          <Button onClick={onClose}>Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Editor Toolbar ─────────────────────────────────────────────────────────────

type ToolbarProps = {
  editor: ReturnType<typeof useEditor>;
  variables: string[];
};

function EditorToolbar({ editor, variables }: ToolbarProps) {
  const [showVarMenu, setShowVarMenu] = useState(false);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const varMenuRef = useRef<HTMLDivElement>(null);
  const colorInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (varMenuRef.current && !varMenuRef.current.contains(e.target as Node)) {
        setShowVarMenu(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!editor) return null;

  const currentFontFamily = editor.getAttributes("textStyle").fontFamily || "Arial";
  const currentFontSize = ((editor.getAttributes("textStyle") as any).fontSize || "14px").replace("px", "");
  const currentColor = editor.getAttributes("textStyle").color || "#000000";

  const insertVariable = (v: string) => {
    editor.chain().focus().insertContent(`{{${v}}}`).run();
    setShowVarMenu(false);
  };

  const applyLink = () => {
    if (linkUrl) {
      editor.chain().focus().setLink({ href: linkUrl }).run();
    } else {
      editor.chain().focus().unsetLink().run();
    }
    setShowLinkInput(false);
    setLinkUrl("");
  };

  const ToolBtn = ({
    active, onClick, title, children,
  }: { active?: boolean; onClick: () => void; title: string; children: React.ReactNode }) => (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onMouseDown={e => { e.preventDefault(); onClick(); }}
            className={cn(
              "w-7 h-7 flex items-center justify-center rounded text-sm transition-colors",
              active
                ? "bg-primary text-primary-foreground"
                : "hover:bg-secondary text-foreground"
            )}
          >
            {children}
          </button>
        </TooltipTrigger>
        <TooltipContent>{title}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  return (
    <div className="border border-border rounded-t-lg bg-secondary/30 px-2 py-1.5 flex flex-wrap items-center gap-1">
      {/* Font Family */}
      <Select
        value={currentFontFamily}
        onValueChange={v => editor.chain().focus().setFontFamily(v).run()}
      >
        <SelectTrigger className="h-7 w-36 text-xs border-border/60" data-testid="select-font-family">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {FONT_FAMILIES.map(f => (
            <SelectItem key={f} value={f} style={{ fontFamily: f }}>
              {f}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Font Size */}
      <Select
        value={currentFontSize}
        onValueChange={v => (editor.chain().focus() as any).setFontSize(`${v}px`).run()}
      >
        <SelectTrigger className="h-7 w-16 text-xs border-border/60" data-testid="select-font-size">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {FONT_SIZES.map(s => (
            <SelectItem key={s} value={s}>{s}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="w-px h-5 bg-border mx-0.5" />

      {/* Bold / Italic / Underline */}
      <ToolBtn active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} title="Gras (Ctrl+B)">
        <Bold className="w-3.5 h-3.5" />
      </ToolBtn>
      <ToolBtn active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italique (Ctrl+I)">
        <Italic className="w-3.5 h-3.5" />
      </ToolBtn>
      <ToolBtn active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Souligné (Ctrl+U)">
        <UnderlineIcon className="w-3.5 h-3.5" />
      </ToolBtn>

      <div className="w-px h-5 bg-border mx-0.5" />

      {/* Text Color */}
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onMouseDown={e => { e.preventDefault(); colorInputRef.current?.click(); }}
              className="w-7 h-7 flex items-center justify-center rounded hover:bg-secondary transition-colors relative"
              title="Couleur du texte"
            >
              <span className="text-xs font-bold" style={{ color: currentColor }}>A</span>
              <span
                className="absolute bottom-0.5 left-1 right-1 h-0.5 rounded-full"
                style={{ backgroundColor: currentColor }}
              />
              <input
                ref={colorInputRef}
                type="color"
                value={currentColor}
                onChange={e => editor.chain().focus().setColor(e.target.value).run()}
                className="sr-only"
                aria-label="Couleur du texte"
              />
            </button>
          </TooltipTrigger>
          <TooltipContent>Couleur du texte</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <div className="w-px h-5 bg-border mx-0.5" />

      {/* Alignment */}
      <ToolBtn active={editor.isActive({ textAlign: "left" })} onClick={() => editor.chain().focus().setTextAlign("left").run()} title="Aligner à gauche">
        <AlignLeft className="w-3.5 h-3.5" />
      </ToolBtn>
      <ToolBtn active={editor.isActive({ textAlign: "center" })} onClick={() => editor.chain().focus().setTextAlign("center").run()} title="Centrer">
        <AlignCenter className="w-3.5 h-3.5" />
      </ToolBtn>
      <ToolBtn active={editor.isActive({ textAlign: "right" })} onClick={() => editor.chain().focus().setTextAlign("right").run()} title="Aligner à droite">
        <AlignRight className="w-3.5 h-3.5" />
      </ToolBtn>

      <div className="w-px h-5 bg-border mx-0.5" />

      {/* Lists */}
      <ToolBtn active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Liste à puces">
        <List className="w-3.5 h-3.5" />
      </ToolBtn>
      <ToolBtn active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Liste numérotée">
        <ListOrdered className="w-3.5 h-3.5" />
      </ToolBtn>

      <div className="w-px h-5 bg-border mx-0.5" />

      {/* Link */}
      <div className="relative">
        <ToolBtn active={editor.isActive("link")} onClick={() => { setLinkUrl(editor.getAttributes("link").href || ""); setShowLinkInput(v => !v); }} title="Insérer un lien">
          <Link2 className="w-3.5 h-3.5" />
        </ToolBtn>
        {showLinkInput && (
          <div className="absolute top-8 left-0 z-50 bg-popover border border-border rounded-lg shadow-lg p-3 flex gap-2 min-w-72">
            <Input
              autoFocus
              value={linkUrl}
              onChange={e => setLinkUrl(e.target.value)}
              placeholder="https://..."
              className="h-7 text-xs"
              onKeyDown={e => { if (e.key === "Enter") applyLink(); if (e.key === "Escape") setShowLinkInput(false); }}
            />
            <Button size="sm" className="h-7 text-xs px-2" onClick={applyLink}>OK</Button>
            {editor.isActive("link") && (
              <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={() => { editor.chain().focus().unsetLink().run(); setShowLinkInput(false); }}>
                Retirer
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="w-px h-5 bg-border mx-0.5" />

      {/* Insert Variable */}
      {variables.length > 0 && (
        <div className="relative" ref={varMenuRef}>
          <button
            type="button"
            onMouseDown={e => { e.preventDefault(); setShowVarMenu(v => !v); }}
            className="h-7 flex items-center gap-1 px-2 rounded text-xs font-medium hover:bg-secondary transition-colors text-primary border border-primary/30"
            data-testid="button-insert-variable"
          >
            <Braces className="w-3 h-3" />
            Variable
            <ChevronDown className="w-3 h-3" />
          </button>
          {showVarMenu && (
            <div className="absolute top-8 left-0 z-50 bg-popover border border-border rounded-lg shadow-lg py-1 min-w-52 max-h-60 overflow-y-auto">
              {variables.map(v => (
                <button
                  key={v}
                  type="button"
                  onMouseDown={e => { e.preventDefault(); insertVariable(v); }}
                  className="w-full text-left px-3 py-1.5 text-xs hover:bg-secondary transition-colors font-mono text-primary"
                  data-testid={`menu-var-${v}`}
                >
                  {`{{${v}}}`}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Email preview wrapper ──────────────────────────────────────────────────────

function buildEmailHtml(bodyHtml: string, settings: EmailSettings): string {
  const footer = [
    settings.footerAdresse,
    `SIRET : ${settings.footerSiret}`,
    `${settings.footerEmail} | ${settings.footerSite}`,
  ].filter(Boolean).join("<br/>");

  let headerBlock: string;
  if (settings.headerMode === "image" && settings.headerImageUrl) {
    headerBlock = `<div style="border-radius:8px 8px 0 0;overflow:hidden;height:120px">
  <img src="${settings.headerImageUrl}" alt="en-tête" style="width:100%;height:120px;object-fit:cover;display:block;" />
</div>`;
  } else {
    const logoBlock = settings.headerLogoUrl
      ? `<img src="${settings.headerLogoUrl}" alt="${settings.headerTexte}" style="height:40px;width:auto;display:block;margin-bottom:6px;" />`
      : "";
    headerBlock = `<div style="background:linear-gradient(to right,${settings.headerGradientStart},${settings.headerGradientEnd});padding:20px;text-align:center;height:120px;box-sizing:border-box;display:flex;flex-direction:column;align-items:center;justify-content:center;border-radius:8px 8px 0 0">
  ${logoBlock}
  <span style="color:#fff;font-size:16px;font-weight:bold;font-family:Arial,sans-serif">${settings.headerTexte}</span>
</div>`;
  }

  return `
<div style="max-width:640px;margin:0 auto;padding:0;font-family:Arial,sans-serif">
  ${headerBlock}
  <div style="background:#fff;padding:32px 28px;line-height:1.7;color:#222">
    ${bodyHtml}
  </div>
  <div style="background:#f9fafb;padding:20px 28px;border-radius:0 0 8px 8px;border-top:1px solid #e5e7eb;text-align:center">
    <p style="font-size:12px;color:#9ca3af;margin:0">${footer}</p>
  </div>
</div>`.trim();
}

// ── Template Editor (WYSIWYG) ─────────────────────────────────────────────────

function TemplateEditor({
  template,
  settings,
  onSaved,
  onClose,
}: {
  template: EmailTemplate;
  settings: EmailSettings;
  onSaved: (t: EmailTemplate) => void;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const [nom, setNom] = useState(template.nom);
  const [objet, setObjet] = useState(template.objet);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [showHtmlModal, setShowHtmlModal] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      TextStyle,
      FontFamily.configure({ types: ["textStyle"] }),
      FontSize.configure({ types: ["textStyle"] }),
      Color.configure({ types: ["textStyle"] }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Link.configure({ openOnClick: false, autolink: true }),
    ],
    content: template.contenuHtml,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      setPreviewHtml(buildEmailHtml(interpolate(html), settings));
    },
  });

  // Init preview on mount
  useEffect(() => {
    if (editor) {
      setPreviewHtml(buildEmailHtml(interpolate(editor.getHTML()), settings));
    }
  }, [editor]);

  // Refresh preview when settings change
  useEffect(() => {
    if (editor) {
      setPreviewHtml(buildEmailHtml(interpolate(editor.getHTML()), settings));
    }
  }, [settings]);

  const currentHtml = editor?.getHTML() ?? template.contenuHtml;

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`/api/email-templates/${template.slug}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nom, objet, contenuHtml: currentHtml }),
      });
      if (!res.ok) throw new Error(await res.text());
      const updated: EmailTemplate = await res.json();
      toast({ title: "Template sauvegardé" });
      onSaved(updated);
    } catch (e: any) {
      toast({ title: "Erreur", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    if (!testEmail) return;
    setSendingTest(true);
    try {
      const res = await authFetch(`/api/email-templates/${template.slug}/test-send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to: testEmail }),
      });
      if (!res.ok) throw new Error((await res.json()).message);
      toast({ title: "Email de test envoyé", description: `Envoyé à ${testEmail}` });
    } catch (e: any) {
      toast({ title: "Erreur envoi test", description: e.message, variant: "destructive" });
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="button-back-templates">
            <ChevronRight className="w-4 h-4 rotate-180" />
          </button>
          <div>
            <h2 className="text-base font-semibold">{template.nom}</h2>
            <p className="text-xs text-muted-foreground">{template.slug}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowPreview(v => !v)}
            data-testid="button-toggle-preview"
          >
            <Eye className="w-4 h-4 mr-1.5" />
            {showPreview ? "Masquer l'aperçu" : "Aperçu"}
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving} data-testid="button-save-template">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
            Enregistrer
          </Button>
        </div>
      </div>

      {/* Nom + Objet */}
      <div className="grid grid-cols-2 gap-3 mb-4 shrink-0">
        <div className="space-y-1">
          <Label className="text-xs">Nom du template</Label>
          <Input value={nom} onChange={e => setNom(e.target.value)} className="h-8 text-sm" data-testid="input-template-nom" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Objet de l'email</Label>
          <Input value={objet} onChange={e => setObjet(e.target.value)} className="h-8 text-sm" data-testid="input-template-objet" />
        </div>
      </div>

      {/* Variables disponibles */}
      {template.variablesDisponibles && template.variablesDisponibles.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3 shrink-0">
          <span className="text-xs text-muted-foreground self-center">Variables :</span>
          {template.variablesDisponibles.map(v => (
            <code
              key={v}
              className="text-xs bg-secondary border border-border rounded px-1.5 py-0.5 cursor-pointer hover:bg-primary/10 transition-colors text-primary select-all"
              title="Cliquer pour copier"
              onClick={() => navigator.clipboard.writeText(`{{${v}}}`)}
              data-testid={`var-chip-${v}`}
            >
              {`{{${v}}}`}
            </code>
          ))}
        </div>
      )}

      {/* Editor + Preview */}
      <div className={cn("flex gap-4 flex-1 min-h-0", showPreview ? "flex-row" : "flex-col")}>
        {/* WYSIWYG Editor */}
        <div className={cn("flex flex-col min-h-0", showPreview ? "w-1/2" : "w-full")}>
          <EditorToolbar editor={editor} variables={template.variablesDisponibles ?? []} />
          <div
            className="flex-1 overflow-y-auto border border-t-0 border-border rounded-b-lg bg-white dark:bg-zinc-900 p-4 text-sm"
            onClick={() => editor?.chain().focus().run()}
          >
            <EditorContent
              editor={editor}
              className="prose prose-sm dark:prose-invert max-w-none focus:outline-none [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[300px]"
            />
          </div>

          {/* Voir le HTML (bas de l'éditeur) */}
          <div className="flex justify-end mt-2 shrink-0">
            <button
              type="button"
              onClick={() => setShowHtmlModal(true)}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
              data-testid="button-view-html"
            >
              <Code2 className="w-3.5 h-3.5" />
              Voir le HTML
            </button>
          </div>
        </div>

        {/* Real-time preview */}
        {showPreview && (
          <div className="w-1/2 flex flex-col min-h-0">
            <p className="text-xs font-medium text-muted-foreground mb-2 shrink-0">Aperçu en temps réel</p>
            <div className="flex-1 overflow-y-auto rounded-lg border border-border bg-[#f5f5f5] p-4">
              <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
            </div>
          </div>
        )}
      </div>

      {/* Test send section */}
      <div className="border-t border-border/50 pt-4 mt-4 shrink-0">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium">Envoyer un email de test</p>
          <p className="text-xs text-muted-foreground">Les variables sont remplacées par des valeurs fictives.</p>
        </div>
        <div className="flex gap-2 mt-2">
          <Input
            type="email"
            placeholder="destinataire@exemple.fr"
            value={testEmail}
            onChange={e => setTestEmail(e.target.value)}
            className="max-w-xs h-8 text-sm"
            data-testid="input-test-email"
            onKeyDown={e => { if (e.key === "Enter") handleSendTest(); }}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={handleSendTest}
            disabled={!testEmail || sendingTest}
            data-testid="button-send-test"
          >
            {sendingTest ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
            Envoyer le test
          </Button>
        </div>
      </div>

      {/* HTML Modal */}
      <HtmlViewModal open={showHtmlModal} html={currentHtml} onClose={() => setShowHtmlModal(false)} />
    </div>
  );
}

// ── Main List Page ─────────────────────────────────────────────────────────────

export default function EmailsPage() {
  const { toast } = useToast();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<EmailTemplate | null>(null);
  const [toggling, setToggling] = useState<string | null>(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [emailSettings, setEmailSettings] = useState<EmailSettings>(DEFAULT_SETTINGS);

  useEffect(() => {
    loadTemplates();
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await authFetch("/api/email-settings");
      if (res.ok) setEmailSettings(await res.json());
    } catch { /* silencieux */ }
  };

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const res = await authFetch("/api/email-templates");
      if (!res.ok) throw new Error(await res.text());
      setTemplates(await res.json());
    } catch (e: any) {
      toast({ title: "Erreur de chargement", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (slug: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setToggling(slug);
    try {
      const res = await authFetch(`/api/email-templates/${slug}/toggle`, { method: "PATCH" });
      if (!res.ok) throw new Error(await res.text());
      const updated: EmailTemplate = await res.json();
      setTemplates(prev => prev.map(t => t.slug === slug ? { ...t, actif: updated.actif } : t));
    } catch (e: any) {
      toast({ title: "Erreur", description: e.message, variant: "destructive" });
    } finally {
      setToggling(null);
    }
  };

  const handleSaved = (updated: EmailTemplate) => {
    setTemplates(prev => prev.map(t => t.slug === updated.slug ? updated : t));
    setSelected(updated);
  };

  const grouped: Record<string, EmailTemplate[]> = {};
  for (const t of templates) {
    if (!grouped[t.categorie]) grouped[t.categorie] = [];
    grouped[t.categorie].push(t);
  }

  if (selected) {
    return (
      <div className="flex flex-col h-[calc(100vh-10rem)]">
        <TemplateEditor
          template={selected}
          settings={emailSettings}
          onSaved={handleSaved}
          onClose={() => setSelected(null)}
        />
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-semibold mb-1">Templates Emails</h2>
          <p className="text-sm text-muted-foreground">
            Personnalisez les emails envoyés automatiquement ou manuellement par la plateforme.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowConfigModal(true)}
          data-testid="button-configure-header-footer"
          className="shrink-0"
        >
          <Settings2 className="w-4 h-4 mr-1.5" />
          Configurer l'en-tête & pied de page
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground py-8">
          <Loader2 className="w-5 h-5 animate-spin" />
          Chargement des templates…
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat}>
              <div className="flex items-center gap-3 mb-3">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  {CATEGORIE_LABELS[cat] || cat}
                </h3>
                <Separator className="flex-1" />
                <span className="text-xs text-muted-foreground">{items.length} template{items.length > 1 ? "s" : ""}</span>
              </div>

              <div className="space-y-2">
                {items.map(t => (
                  <button
                    key={t.slug}
                    onClick={() => setSelected(t)}
                    data-testid={`row-template-${t.slug}`}
                    className="w-full text-left rounded-xl border border-border/60 bg-card hover:border-primary/40 hover:bg-secondary/30 transition-all px-4 py-3.5 flex items-center gap-4 group"
                  >
                    <div className={cn(
                      "w-9 h-9 rounded-lg flex items-center justify-center shrink-0",
                      t.actif ? "bg-primary/10" : "bg-secondary"
                    )}>
                      <Mail className={cn("w-4 h-4", t.actif ? "text-primary" : "text-muted-foreground")} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm truncate">{t.nom}</p>
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 shrink-0">
                          {DECLENCHEUR_LABELS[t.declencheur] || t.declencheur}
                        </Badge>
                      </div>
                      {t.description && (
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">{t.description}</p>
                      )}
                      <p className="text-xs text-muted-foreground/70 mt-0.5">
                        Objet : <span className="text-muted-foreground">{t.objet.length > 70 ? t.objet.slice(0, 70) + "…" : t.objet}</span>
                      </p>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                      {t.actif ? (
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                      ) : (
                        <XCircle className="w-3.5 h-3.5 text-muted-foreground/50" />
                      )}
                      <div onClick={e => handleToggle(t.slug, e)} data-testid={`toggle-template-${t.slug}`}>
                        {toggling === t.slug ? (
                          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                        ) : (
                          <Switch checked={t.actif} onCheckedChange={() => {}} className="pointer-events-none" />
                        )}
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && templates.length === 0 && (
        <div className="text-center py-16 text-muted-foreground">
          <Mail className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>Aucun template trouvé.</p>
        </div>
      )}

      <HeaderFooterModal
        open={showConfigModal}
        initial={emailSettings}
        onClose={() => setShowConfigModal(false)}
        onSaved={s => setEmailSettings(s)}
      />
    </div>
  );
}
